import { useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldAlert } from "lucide-react";

/**
 * Pop-up de "Senha de Autorização" — aceita credenciais de QUALQUER usuário
 * que tenha permissão de `action` em `resource`. Se resource/action não forem
 * passados, cai no comportamento antigo (só a senha do próprio usuário logado).
 *
 * Props:
 *   open, onClose, onConfirmed, title, message
 *   resource (opcional): "entregas" | "bairros" | ... (usa PERM_RESOURCES do backend)
 *   action (default "e"): "v" | "i" | "a" | "e"
 */
export function PasswordConfirm({
  open, onClose, onConfirmed,
  title = "Confirmar exclusão",
  message = "Digite a senha de um usuário autorizado a excluir:",
  resource = null, action = "e",
}) {
  const [identifier, setIdentifier] = useState("");
  const [senha, setSenha] = useState("");
  const [loading, setLoading] = useState(false);
  const needsAuthorizer = !!resource;

  const submit = async (e) => {
    e?.preventDefault();
    if (!senha) return toast.error("Senha obrigatória");
    if (needsAuthorizer && !identifier) return toast.error("Informe o e-mail, nome ou usuário do autorizador");
    setLoading(true);
    try {
      if (needsAuthorizer) {
        // Envia como `identifier` — backend aceita email/nome/username
        const r = await api.post("/auth/verify-authorizer", { identifier, senha, resource, action });
        if (!r.data.ok) throw new Error("Autorização negada");
        toast.success(`Autorizado por ${r.data.authorizer}`);
      } else {
        const r = await api.post("/auth/verify-password", { senha });
        if (!r.data.can_delete) { toast.error("Usuário sem permissão para excluir"); return; }
      }
      const senhaOk = senha;
      setSenha(""); setIdentifier("");
      onConfirmed(senhaOk);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Credenciais inválidas");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { setSenha(""); setIdentifier(""); onClose(); } }}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="flex items-center gap-2">
          <ShieldAlert className="w-5 h-5 text-rose-600" />{title}
        </DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <p className="text-sm text-muted-foreground">{message}</p>
          {needsAuthorizer && (
            <div><Label htmlFor="pwd-identifier">E-mail, nome ou usuário do autorizador</Label>
              <Input id="pwd-identifier" data-testid="pwd-identifier-input" type="text"
                autoCapitalize="none" autoCorrect="off" spellCheck={false} autoFocus
                value={identifier} onChange={(e) => setIdentifier(e.target.value)}
                placeholder="email@empresa.com ou seu usuário" /></div>
          )}
          <div><Label htmlFor="pwd-confirm">Senha</Label>
            <Input id="pwd-confirm" data-testid="pwd-confirm-input" type="password" autoFocus={!needsAuthorizer}
              value={senha} onChange={(e) => setSenha(e.target.value)} /></div>
          {needsAuthorizer && (
            <div className="text-[11px] text-muted-foreground bg-amber-50 border border-amber-200 rounded p-2">
              💡 Aceita <b>e-mail</b>, <b>nome completo</b> ou <b>nome de usuário</b> de qualquer pessoa que tenha permissão de {action === "e" ? "excluir" : action} em {resource}.
            </div>
          )}
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={loading} className="bg-rose-600 hover:bg-rose-700" data-testid="pwd-confirm-submit">
              {loading ? "Verificando..." : "Confirmar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
